import type { ColorScale, PublicTenantConfig, TenantSeoConfig } from './types'

export const WALMART_BRAND: ColorScale = [
    '#E9F1FE',
    '#C9E3FD',
    '#A9DDF7',
    '#7FCBF7',
    '#4DBDF5',
    '#1E86EE',
    '#0053E2',
    '#0044B8',
    '#002E86',
    '#001E60',
] as const

export const WALMART_SPARK_YELLOW = '#FFC220'

const PRIMARY_CARE_VISIT_SKU_ID = '1400017'
const PRESCRIPTION_RENEWAL_SKU_ID = '1400018'
const URGENT_CARE_SKU_ID = '1400015'
const PRESCRIPTION_RENEWAL_EXISTING_PATIENT_SKU_ID = '1400036'
const FOLLOW_UP_VIDEO_VISIT_SKU_ID = '1400035'
const PRIMARY_CARE_VISIT_HREF = `/s/primary-care-visit/${PRIMARY_CARE_VISIT_SKU_ID}`

const FAVICON_PATH = '/tenants/walmart/favicons'

/** Production origin for Walmart Better Care Services canonical and share URLs. */
export const WALMART_CANONICAL_BASE = 'https://bettercareservices.walmart.com'

const WALMART_SEO: TenantSeoConfig = {
    canonicalBase: WALMART_CANONICAL_BASE,
    defaultDescription:
        'Find the support you need. Connect with expert providers 24/7 for guidance and care to feel your best.',
    ogImage: '/tenants/walmart/og-image.jpg',
    brandPageTitles: true,
    noindex: false,
    icons: {
        icon: [
            { url: `${FAVICON_PATH}/favicon.ico`, type: 'image/x-icon' },
            ...[16, 32, 96, 196].map((size) => ({
                url: `${FAVICON_PATH}/favicon-${size}x${size}.png`,
                sizes: `${size}x${size}`,
                type: 'image/png',
            })),
        ],
        apple: [180, 152, 120, 76, 60].map((size) => ({
            url: `${FAVICON_PATH}/apple-touch-icon-${size}x${size}.png`,
            sizes: `${size}x${size}`,
        })),
        other: [192, 512].map((size) => ({
            rel: 'icon',
            url: `${FAVICON_PATH}/android-chrome-${size}x${size}.png`,
            sizes: `${size}x${size}`,
        })),
    },
}

// Client-safe: keep component imports out, since `walmart.ts` adds the server-only AppLayout.
export const walmartPublic: PublicTenantConfig = {
    id: 'walmart',
    brand: {
        displayName: 'Better Care Services',
        supportEmail: 'bettercareservices@generalmedicine.co',
    },
    themeColor: '#e1f3f8',
    seo: WALMART_SEO,
    tokens: {
        colors: {
            brand: WALMART_BRAND,
            highlight: {
                background: 'var(--walmart-action-fill-strong)',
                foreground: 'var(--gm-foreground-fg-primary)',
                backgroundSubtle: 'var(--gm-gray-1)',
            },
            gradient: {
                highlightFrom: WALMART_BRAND[4],
            },
        },
    },
    capabilities: {
        generalAi: false,
        multiFulfillmentOwner: true,
        urgentCare: true,
        nativeAppExternalDeeplinks: true,
        directToS3FileUpload: true,
        textCareTeam: false,
        medicalOrganizationSchema: false,
        gmCard: false,
        recruitingHtmlComment: false,
        medicationSearch: false,
    },
    constants: {
        sku: {
            bookVisitSkuId: PRIMARY_CARE_VISIT_SKU_ID,
            bookVisitDetailHref: PRIMARY_CARE_VISIT_HREF,
            bookVisitHref: `${PRIMARY_CARE_VISIT_HREF}/location-confirmation`,
            primaryCareVisitSkuId: PRIMARY_CARE_VISIT_SKU_ID,
            primaryCareVisitHref: PRIMARY_CARE_VISIT_HREF,
            prescriptionRenewalSkuId: PRESCRIPTION_RENEWAL_SKU_ID,
            prescriptionRenewalHref: `/s/prescription-renewal/${PRESCRIPTION_RENEWAL_SKU_ID}`,
            prescriptionRenewalExistingPatientSkuId: PRESCRIPTION_RENEWAL_EXISTING_PATIENT_SKU_ID,
            followUpVideoVisitSkuId: FOLLOW_UP_VIDEO_VISIT_SKU_ID,
            urgentCareSkuId: URGENT_CARE_SKU_ID,
        },
    },
}
